import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SignUp, SignIn, useAuth } from '@clerk/clerk-react';
import { useApp } from '../context/AppContext.jsx';
import {
  ENNEAGRAM_QUESTIONS,
  scoreAssessment,
} from '../data/enneagramAssessment.js';
import useVoiceConversation from '../hooks/useVoiceConversation.js';
import FoxMark from '../components/shared/FoxMark.jsx';
import RootedWordmark from '../components/shared/RootedWordmark.jsx';

const PAPER = '#F2EBDC';
const INK = '#1B1814';
const GOLD = '#A67C3A';

// Duty-of-care disclaimer. Verbatim copy of
// Intake.jsx:COMPLETION_DISCLAIMER_TEXT — the old end-of-intake card
// surfaced this on its own screen; the redesigned flow merges it into
// the Enneagram intro so the user reads it once, here, before the
// assessment begins. Day 7 cleanup deletes the source constant in
// Intake.jsx; until then the text is duplicated across both files.
const DUTY_OF_CARE_DISCLAIMER = `Before we go further — Rooted is built on scripture and the wisdom of people who have walked hard roads. But I am not a counselor and this is not a substitute for one. If you are carrying something that needs more than a study, please reach out to someone who can truly sit with you. What is ahead here matters. And I am honored to be part of it.`;

// Gender → ElevenLabs voice ID map. Mirrors the same constant in
// Intake.jsx and DayDetail.jsx; consolidating into a shared module is
// a Day 7 cleanup (see TODO).
const VOICE_IDS = {
  male: 'L0Dsvb3SLTyegXwtm47J',
  female: 'ys3XeJJA4ArWMhRpcX1D',
};

const ACKNOWLEDGMENTS = [
  'Got it. That tells me something important.',
  "Interesting. That's a revealing answer.",
  'Yeah — that one says a lot.',
  'Appreciate you being honest with that one.',
  'That tracks. Next one.',
  'Good. That paints a clear picture.',
  'Noted. Keep going.',
];

export default function EnneagramAssessment() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromVoice = location.state?.fromVoice === true;
  const { isSignedIn } = useAuth();
  const { guide, updateUserProfile } = useApp();

  // Day-5 audio: useVoiceConversation owns the AudioContext and TTS
  // pipeline via the singleton AudioSessionManager. Pre-auth shape —
  // authToken null because the assessment runs before the Clerk
  // sign-up prompt; persistence is IDB-only and isn't used here (no
  // onTurnComplete since we don't listen for mic turns on this route).
  const voice = useVoiceConversation({
    userId: null,
    authToken: null,
    sessionId: null,
    voiceId: VOICE_IDS[guide?.gender] || VOICE_IDS.female,
    onError: (err) => {
      console.error('[Enneagram/voice]', err);
    },
  });

  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState('intro'); // intro | asking | complete
  // Sub-state within the intro phase. 'disclaimer' shows only the
  // duty-of-care card + an "I understand" button so acknowledgment is
  // explicit; 'intro' shows the existing headline / description /
  // read-mode toggle / Begin button. Only the "I understand" tap
  // advances disclaimer → intro.
  const [introStep, setIntroStep] = useState('disclaimer');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [readMode, setReadMode] = useState('silent'); // 'voice' | 'silent'
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('signup'); // 'signup' | 'signin'
  const readModeRef = useRef('silent');
  const hasNavigatedRef = useRef(false);

  // On mount — if Clerk reports signed-in AND auth-completed flag is set,
  // this is the post-sign-up bounce. Forward to /preparing immediately.
  useEffect(() => {
    let authCompleted = false;
    try {
      authCompleted =
        localStorage.getItem('rooted:auth:completed') === 'true';
    } catch {}
    if (isSignedIn && authCompleted) {
      console.log(
        '[Enneagram] Already signed in on mount — redirecting to /preparing'
      );
      navigate('/preparing', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for sign-in / sign-up completion while the auth overlay is
  // visible. Clerk's virtual routing does not redirect — instead
  // isSignedIn flips to true. Guarded by hasNavigatedRef so we only
  // fire once.
  useEffect(() => {
    if (!isSignedIn) return;
    if (!showAuth) return;
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    console.log('[Enneagram] auth complete — navigating to /preparing');
    try {
      localStorage.setItem('rooted:auth:completed', 'true');
    } catch {}
    setTimeout(() => navigate('/preparing'), 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, showAuth]);

  // Speak the duty-of-care disclaimer on intro mount when the user
  // arrived from voice-mode intake. Independent of the "Read to me /
  // Read myself" toggle (which only governs assessment-question
  // delivery). Bypasses the local speak() helper because that helper
  // gates on readMode.
  const disclaimerSpokenRef = useRef(false);
  useEffect(() => {
    if (phase !== 'intro') return;
    if (introStep !== 'disclaimer') return;
    if (disclaimerSpokenRef.current) return;
    if (!fromVoice) return;
    disclaimerSpokenRef.current = true;
    (async () => {
      try {
        await voice.init();
        await voice.speak(DUTY_OF_CARE_DISCLAIMER);
      } catch (err) {
        console.warn('[Enneagram] disclaimer speak failed:', err?.message);
      }
    })();
  }, [phase, introStep, fromVoice]);

  const question = ENNEAGRAM_QUESTIONS[currentQ];
  const progress = Math.round((currentQ / ENNEAGRAM_QUESTIONS.length) * 100);

  function chooseReadMode(mode) {
    setReadMode(mode);
    readModeRef.current = mode;
    if (mode === 'voice') {
      // Toggle tap is the first user gesture in the audio session when
      // the user came from text-mode intake. Calling init here claims
      // the iOS audio session under that gesture. Idempotent in the
      // voice-from-intake path where the session is already claimed.
      // reset() first recovers from CANCELLED — e.g. user tapped
      // "I understand" mid-disclaimer, which left the state machine
      // cancelled and init() would otherwise throw.
      try {
        voice.reset();
      } catch {}
      voice.init().catch((err) => {
        console.warn(
          '[Enneagram] init on chooseReadMode failed:',
          err?.message
        );
      });
    }
  }

  function cancelCurrentSpeech() {
    try {
      voice.cancel();
    } catch (e) {
      console.warn('[Enneagram] voice.cancel failed:', e?.message);
    }
    // cancel() leaves the state machine in CANCELLED. reset() returns
    // it to UNLOCKED so the next speak()/init() doesn't throw. reset()
    // itself throws if state isn't CANCELLED/ERROR — wrap to swallow.
    try {
      voice.reset();
    } catch {}
    setIsSpeaking(false);
  }

  async function speak(text, onEnd) {
    if (readModeRef.current !== 'voice') {
      if (onEnd) onEnd();
      return;
    }
    // Interrupt any prior in-flight speak before starting the next one
    // (matches the legacy cancelCurrentSpeech ordering inside speak).
    try {
      voice.cancel();
    } catch {}
    // Recover from the CANCELLED state cancel() leaves behind so the
    // upcoming init() doesn't throw. No-op if state wasn't cancellable.
    try {
      voice.reset();
    } catch {}
    setIsSpeaking(true);
    try {
      // Defensive init — idempotent if the audio session is already
      // claimed (it usually is, by Intake), recovers a suspended
      // context if iOS interrupted during the route transition.
      await voice.init();
      await voice.speak(text);
    } catch (err) {
      console.warn('[Enneagram] voice.speak failed:', err?.message);
    } finally {
      setIsSpeaking(false);
      if (onEnd) onEnd();
    }
  }

  // Advances introStep from 'disclaimer' to 'intro'. Cancels any
  // in-flight disclaimer speech — if the user has tapped "I understand"
  // they've signaled they want to move on; respect that.
  const handleAcceptDisclaimer = () => {
    cancelCurrentSpeech();
    setIntroStep('intro');
  };

  async function startAssessment() {
    setPhase('asking');
    if (readModeRef.current === 'voice') {
      await speak(
        `Alright — I'm going to ask you ten questions. There are no right or wrong answers. What I'm trying to understand is how you're actually wired — what drives you, what frustrates you, how you naturally think. Answer with whatever feels most true, not what sounds best. Go with your gut. Ready? Here's the first one.`,
        () => askCurrentQuestion(0)
      );
    } else {
      // Silent mode — questions are visible immediately, no spoken intro.
    }
  }

  async function askCurrentQuestion(qIndex) {
    if (readModeRef.current !== 'voice') return;
    const q = ENNEAGRAM_QUESTIONS[qIndex];
    // Only read the question — NOT the options. Options are visible and
    // tappable from the moment the question paints.
    await speak(q.question);
  }

  async function handleAnswer(letter) {
    // Tapping always interrupts the current speech.
    cancelCurrentSpeech();
    setSelectedOption(letter);

    const newAnswers = { ...answers, [question.id]: letter };
    setAnswers(newAnswers);

    const ack =
      ACKNOWLEDGMENTS[Math.floor(Math.random() * ACKNOWLEDGMENTS.length)];

    if (currentQ < ENNEAGRAM_QUESTIONS.length - 1) {
      const nextIndex = currentQ + 1;
      const advance = () => {
        setSelectedOption(null);
        setCurrentQ(nextIndex);
        setTimeout(() => askCurrentQuestion(nextIndex), 250);
      };
      if (readModeRef.current === 'voice') {
        await speak(ack, advance);
      } else {
        advance();
      }
    } else {
      const finish = () => {
        const result = scoreAssessment(newAnswers);
        deliverResults(result);
      };
      if (readModeRef.current === 'voice') {
        await speak(ack, finish);
      } else {
        finish();
      }
    }
  }

  async function deliverResults(result) {
    setPhase('complete');

    const { enneagram_type, wing, label, confidence } = result;

    updateUserProfile({
      enneagram_type,
      enneagram_wing: wing,
      enneagram_label: label,
      enneagram_confidence: confidence,
      enneagram_scores: result.scores,
    });

    const resultMsg = `Alright — I've got a clear picture. You're a Type ${enneagram_type} with a ${wing} wing — ${label}. ${
      confidence === 'low'
        ? 'Your answers pointed in a couple of directions, so take this as a starting point.'
        : 'Your answers were very consistent — this is clear.'
    } I want to save this for you so it is waiting every time you come back. Let me ask you to create a quick account before we build your study.`;

    const finishOrAuth = () => {
      if (isSignedIn) {
        try {
          localStorage.setItem('rooted:auth:completed', 'true');
        } catch {}
        setTimeout(() => navigate('/preparing'), 1000);
        return;
      }
      setShowAuth(true);
    };

    if (readModeRef.current === 'voice') {
      await speak(resultMsg, finishOrAuth);
    } else {
      setTimeout(finishOrAuth, 800);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: PAPER,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 30,
          background: PAPER,
          borderBottom: '1px solid rgba(166,124,58,0.15)',
          padding: '12px 20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: GOLD,
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {phase === 'intro'
              ? 'Enneagram Assessment'
              : phase === 'complete'
                ? 'Complete'
                : `Question ${currentQ + 1} of ${ENNEAGRAM_QUESTIONS.length}`}
          </span>
          <span
            style={{ fontSize: 11, color: GOLD, fontFamily: 'monospace' }}
          >
            {progress}%
          </span>
        </div>
        <div
          style={{
            height: 3,
            background: 'rgba(166,124,58,0.15)',
            borderRadius: 2,
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: GOLD,
              borderRadius: 2,
              transition: 'width 0.5s ease',
            }}
          />
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 24px 40px',
          maxWidth: 480,
          margin: '0 auto',
          width: '100%',
        }}
      >
        {phase === 'intro' && introStep === 'disclaimer' && (
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ marginBottom: 16 }}>
              <RootedWordmark size="sm" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <FoxMark size={52} />
            </div>

            {/* Duty-of-care disclaimer. Moved here from the legacy
                end-of-intake completion card so the user reads it once,
                up front, before the assessment begins. */}
            <div
              style={{
                background: 'rgba(166,124,58,0.08)',
                border: '1px solid rgba(166,124,58,0.25)',
                borderRadius: 12,
                padding: '16px 18px',
                marginBottom: 24,
                maxWidth: 380,
                marginLeft: 'auto',
                marginRight: 'auto',
                textAlign: 'left',
              }}
            >
              <p
                style={{
                  fontSize: 13.5,
                  color: INK,
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  lineHeight: 1.65,
                  margin: 0,
                  opacity: 0.85,
                }}
              >
                {DUTY_OF_CARE_DISCLAIMER}
              </p>
            </div>

            <button
              type="button"
              onClick={handleAcceptDisclaimer}
              style={{
                background: INK,
                color: PAPER,
                border: 'none',
                borderRadius: 12,
                padding: '14px 40px',
                fontSize: 16,
                cursor: 'pointer',
                fontWeight: 600,
                marginBottom: 12,
                width: '100%',
                maxWidth: 340,
              }}
            >
              I understand
            </button>
          </div>
        )}

        {phase === 'intro' && introStep === 'intro' && (
          <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{ marginBottom: 16 }}>
              <RootedWordmark size="sm" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <FoxMark size={52} />
            </div>

            <h2
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 26,
                fontWeight: 300,
                color: INK,
                marginBottom: 12,
                lineHeight: 1.4,
              }}
            >
              Let's go a little deeper
            </h2>
            <p
              style={{
                fontSize: 15,
                color: INK,
                opacity: 0.6,
                lineHeight: 1.7,
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                marginBottom: 24,
              }}
            >
              Ten questions about how you're wired. Takes about 3-4 minutes.
              The more honest you are, the more specific your study becomes.
            </p>

            <div
              style={{
                display: 'flex',
                gap: 10,
                marginBottom: 24,
                width: '100%',
                maxWidth: 340,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <button
                type="button"
                onClick={() => chooseReadMode('voice')}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  background: readMode === 'voice' ? INK : 'transparent',
                  border: `1.5px solid ${readMode === 'voice' ? INK : 'rgba(166,124,58,0.3)'}`,
                  color: readMode === 'voice' ? PAPER : INK,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'Georgia, serif',
                }}
              >
                🔊 Read to me
              </button>
              <button
                type="button"
                onClick={() => chooseReadMode('silent')}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  background: readMode === 'silent' ? INK : 'transparent',
                  border: `1.5px solid ${readMode === 'silent' ? INK : 'rgba(166,124,58,0.3)'}`,
                  color: readMode === 'silent' ? PAPER : INK,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'Georgia, serif',
                }}
              >
                👁 Read myself
              </button>
            </div>

            <button
              type="button"
              onClick={startAssessment}
              style={{
                background: INK,
                color: PAPER,
                border: 'none',
                borderRadius: 12,
                padding: '14px 40px',
                fontSize: 16,
                cursor: 'pointer',
                fontWeight: 600,
                marginBottom: 12,
                width: '100%',
                maxWidth: 340,
              }}
            >
              Begin assessment
            </button>
            {/* Skip button removed — Enneagram is now required for new
                users (the EnneagramRequired guard in App.jsx enforces
                this for /preparing, /study, /journal, /journey). */}
          </div>
        )}

        {phase === 'asking' && question && (
          <div style={{ width: '100%' }}>
            {isSpeaking && readMode === 'voice' && (
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      color: GOLD,
                      fontFamily: 'Georgia, serif',
                      fontStyle: 'italic',
                    }}
                  >
                    {guide?.name || 'Your guide'} is speaking — tap to skip
                  </span>
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: GOLD,
                        animation: `typing-dot 1.2s ease-in-out ${i * 0.4}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <p
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 18,
                color: INK,
                lineHeight: 1.6,
                marginBottom: 24,
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              {question.question}
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {question.options.map((option) => (
                <button
                  key={option.letter}
                  type="button"
                  onClick={() => handleAnswer(option.letter)}
                  style={{
                    background:
                      selectedOption === option.letter ? INK : 'white',
                    color: selectedOption === option.letter ? PAPER : INK,
                    border: `1.5px solid ${selectedOption === option.letter ? INK : 'rgba(166,124,58,0.25)'}`,
                    borderRadius: 12,
                    padding: '14px 16px',
                    fontSize: 14,
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    transition: 'all 0.15s ease',
                    fontFamily: 'Georgia, serif',
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      minWidth: 20,
                      color: selectedOption === option.letter ? PAPER : GOLD,
                    }}
                  >
                    {option.letter}
                  </span>
                  {option.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === 'complete' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 20 }}>
              <FoxMark variant="gold" size={52} />
            </div>
            <h2
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: 24,
                fontWeight: 300,
                color: INK,
                marginBottom: 8,
              }}
            >
              Building your study now
            </h2>
            <p
              style={{
                fontSize: 14,
                color: INK,
                opacity: 0.5,
                fontStyle: 'italic',
                fontFamily: 'Georgia, serif',
              }}
            >
              Using everything you shared to personalize your first week...
            </p>
          </div>
        )}
      </div>

      {showAuth && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: PAPER,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            zIndex: 50,
            padding: '40px 20px',
            overflowY: 'auto',
          }}
        >
          <div style={{ marginBottom: 20 }}>
            <FoxMark variant="gold" size={44} />
          </div>

          <h2
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 24,
              fontWeight: 300,
              color: INK,
              textAlign: 'center',
              margin: '0 0 12px',
              lineHeight: 1.4,
              maxWidth: 340,
            }}
          >
            Save your results and your study
          </h2>

          <p
            style={{
              fontSize: 14,
              color: INK,
              opacity: 0.6,
              textAlign: 'center',
              lineHeight: 1.7,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              marginBottom: 8,
              maxWidth: 320,
            }}
          >
            Your Enneagram profile and personalized study will be saved to
            your account — available any time, on any device.
          </p>

          <div
            style={{
              background: 'rgba(166,124,58,0.08)',
              border: '1px solid rgba(166,124,58,0.2)',
              borderRadius: 12,
              padding: '12px 16px',
              marginBottom: 24,
              maxWidth: 340,
              width: '100%',
            }}
          >
            <p
              style={{
                fontSize: 12,
                color: INK,
                opacity: 0.6,
                lineHeight: 1.6,
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                margin: 0,
                textAlign: 'center',
              }}
            >
              Completely private. Never shared. Takes about 30 seconds.
            </p>
          </div>

          {authMode === 'signup' ? (
            <SignUp
              routing="virtual"
              fallbackRedirectUrl="/preparing"
              appearance={{
                variables: {
                  colorPrimary: GOLD,
                  colorBackground: PAPER,
                  colorText: INK,
                  fontFamily: 'Georgia, serif',
                  borderRadius: '12px',
                },
                elements: {
                  card: {
                    boxShadow: 'none',
                    background: 'transparent',
                    border: '1px solid rgba(166,124,58,0.2)',
                    borderRadius: '16px',
                    padding: '24px',
                  },
                  rootBox: { width: '100%', maxWidth: '400px' },
                  headerTitle: { display: 'none' },
                  headerSubtitle: { display: 'none' },
                  socialButtonsBlockButton: { display: 'none' },
                  dividerRow: { display: 'none' },
                },
              }}
            />
          ) : (
            <SignIn
              routing="virtual"
              fallbackRedirectUrl="/preparing"
              appearance={{
                variables: {
                  colorPrimary: GOLD,
                  colorBackground: PAPER,
                  colorText: INK,
                  fontFamily: 'Georgia, serif',
                  borderRadius: '12px',
                },
                elements: {
                  card: {
                    boxShadow: 'none',
                    background: 'transparent',
                    border: '1px solid rgba(166,124,58,0.2)',
                    borderRadius: '16px',
                    padding: '24px',
                  },
                  rootBox: { width: '100%', maxWidth: '400px' },
                  headerTitle: { display: 'none' },
                  headerSubtitle: { display: 'none' },
                  socialButtonsBlockButton: { display: 'none' },
                  dividerRow: { display: 'none' },
                },
              }}
            />
          )}

          <button
            type="button"
            onClick={() =>
              setAuthMode(authMode === 'signup' ? 'signin' : 'signup')
            }
            style={{
              background: 'none',
              border: 'none',
              color: GOLD,
              fontSize: 13,
              cursor: 'pointer',
              textDecoration: 'underline',
              marginTop: 12,
            }}
          >
            {authMode === 'signup'
              ? 'Already have an account? Sign in'
              : 'New to Rooted? Create account'}
          </button>
        </div>
      )}

      <style>{`
        @keyframes typing-dot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
